import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { useCallback } from 'react'

interface SignInResult {
  error: string | null
}

interface SignUpResult {
  error: string | null
  requiresConfirmation: boolean
}

export function useAuth() {
  const { user, loading, signOut } = useAuthStore()

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) return { error: mapAuthError(error.message) }
    return { error: null }
  }, [])

  const signUp = useCallback(async (email: string, password: string): Promise<SignUpResult> => {
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) return { error: mapAuthError(error.message), requiresConfirmation: false }
    return { error: null, requiresConfirmation: true }
  }, [])

  const signInWithGoogle = useCallback(async (): Promise<SignInResult> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    })
    if (error) return { error: mapAuthError(error.message) }
    // On success the browser redirects; no further action needed
    return { error: null }
  }, [])

  const requestPasswordReset = useCallback(async (email: string): Promise<SignInResult> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) return { error: mapAuthError(error.message) }
    return { error: null }
  }, [])

  const updatePassword = useCallback(async (password: string): Promise<SignInResult> => {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) return { error: mapAuthError(error.message) }
    return { error: null }
  }, [])

  return {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    requestPasswordReset,
    updatePassword,
  }
}

function mapAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) return 'Incorrect email or password.'
  if (message.includes('Email not confirmed')) return 'Please confirm your email before signing in.'
  if (message.includes('User already registered'))
    return 'An account with this email already exists. Try signing in.'
  if (message.includes('Password should be')) return 'Password must be at least 8 characters.'
  if (message.includes('rate limit'))
    return 'Too many attempts. Please wait a moment and try again.'
  return 'Something went wrong. Please try again.'
}
